import type { CSSProperties } from 'react';

/**
 * WwaaveLogo — the WWAAVE brand mark, rendered inline (NOT via <img>) so the
 * Darker Grotesque wordmark renders in-font and dark mode swaps the wordmark
 * colour via the `ink` token (no `filter: invert` hack on the colourful ripple).
 *
 * "A spark dropped in water": a warm Clay centre dropped into cool Tide rings,
 * open at the top (the gap where the spark broke the surface). The mark colours
 * are fixed (clay + teal read on any ground); only the wordmark tracks the scheme.
 *
 * Variants:
 *  - `horizontal` (default): mark + "wwaave" wordmark, side by side.
 *  - `mark`: the ripple only (square).
 *  - `stacked`: mark above the wordmark.
 *
 * Sizing is driven by `height` (the mark's box). Below ~28px the inner Shallow
 * arc is dropped for legibility (Brand Pack v3 §3 min size: mark 22px).
 */

type Variant = 'horizontal' | 'mark' | 'stacked';

const CLAY = '#D2602E';
const SHALLOW = '#33A093';
const TIDE = '#2A7E78';
const DEEP = '#235E59';

export function WwaaveMark({
  size = 28,
  reversed = false,
  style,
}: {
  size?: number;
  /** Render the dot in Paper instead of Clay (for use on a Clay/Sun fill). */
  reversed?: boolean;
  style?: CSSProperties | undefined;
}) {
  const small = size < 28;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      fill="none"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block', flex: 'none', ...style }}
    >
      <circle cx="60" cy="60" r={6.5} fill={reversed ? '#F7F1E7' : CLAY} />
      {!small && (
        <path
          d="M68.13 41.73A20 20 0 1 1 51.87 41.73"
          fill="none"
          stroke={SHALLOW}
          strokeWidth={7}
          strokeLinecap="round"
        />
      )}
      <path
        d="M73.83 28.94A34 34 0 1 1 46.17 28.94"
        fill="none"
        stroke={TIDE}
        strokeWidth={small ? 7.5 : 6.5}
        strokeLinecap="round"
      />
      <path
        d="M79.52 16.15A48 48 0 1 1 40.48 16.15"
        fill="none"
        stroke={DEEP}
        strokeWidth={small ? 7 : 6}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function WwaaveLogo({
  height = 32,
  variant = 'horizontal',
  style,
}: {
  height?: number;
  variant?: Variant;
  style?: CSSProperties | undefined;
}) {
  // Wordmark: Darker Grotesque 900, lowercase, tracking tight. Colour tracks the
  // colour scheme via the inverted `ink` token (near-black light / near-white dark).
  const wordmark = (
    <span
      style={{
        fontFamily: 'var(--wwaave-font-wordmark)',
        fontWeight: 900,
        fontSize: height * 1.16,
        lineHeight: 1,
        letterSpacing: '-0.01em',
        color: 'var(--mantine-color-ink-9)',
        display: 'inline-block',
        // optical baseline nudge so the wordmark sits centred against the mark
        transform: 'translateY(-0.06em)',
      }}
    >
      wwaave
    </span>
  );

  if (variant === 'mark') {
    return <WwaaveMark size={height} style={style} />;
  }

  if (variant === 'stacked') {
    return (
      <span
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: height * 0.18,
          ...style,
        }}
      >
        <WwaaveMark size={height} />
        {wordmark}
      </span>
    );
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: height * 0.34,
        ...style,
      }}
    >
      <WwaaveMark size={height} />
      {wordmark}
    </span>
  );
}
